import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Calendar, BarChart3, Lock } from "lucide-react";

const PORTAL_PIN = "1021";
const AUTH_KEY = "h2g_portal_auth";
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

const isAuthenticated = () => {
  const stored = localStorage.getItem(AUTH_KEY);
  if (!stored) return false;
  try {
    const { expiry } = JSON.parse(stored);
    return Date.now() < expiry;
  } catch { return false; }
};

const HomePage = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (pin === PORTAL_PIN) {
      localStorage.setItem(AUTH_KEY, JSON.stringify({ expiry: Date.now() + TWO_WEEKS_MS }));
      setAuthed(true);
      setError(false);
    } else {
      setError(true);
      setPin("");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Hybrid2Go Portal</h1>
          <p className="text-sm text-gray-500 mb-6">Enter PIN to access</p>
          <form onSubmit={handleUnlock}>
            <input
              type="password"
              inputMode="numeric"
              maxLength={10}
              value={pin}
              onChange={(e) => { setPin(e.target.value); setError(false); }}
              placeholder="Enter PIN"
              className={`w-full text-center text-2xl tracking-[0.3em] font-bold py-3 border-2 rounded-xl focus:outline-none focus:border-blue-500 ${error ? "border-red-400 bg-red-50" : "border-gray-300"}`}
              autoFocus
              data-testid="portal-pin-input"
            />
            {error && <p className="text-red-500 text-sm mt-2 font-medium">Incorrect PIN</p>}
            <button
              type="submit"
              className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
              data-testid="portal-unlock-btn"
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Hybrid2Go Internal Portal
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Select a system to access
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Warranty Claims Card */}
          <div
            onClick={() => navigate("/dashboard")}
            className="bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 cursor-pointer group p-8"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-500 transition-colors">
                <ClipboardList className="w-10 h-10 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Warranty Claims Management
              </h2>
              <p className="text-gray-600 mb-6">
                View, manage, and track warranty claims. Update statuses, send messages, and handle customer requests.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                  Claims Dashboard
                </span>
                <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                  Status Updates
                </span>
                <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                  Messaging
                </span>
              </div>
            </div>
          </div>

          {/* Booking System Card */}
          <div
            onClick={() => navigate("/booking")}
            className="bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-green-500 hover:shadow-2xl transition-all duration-300 cursor-pointer group p-8"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-green-500 transition-colors">
                <Calendar className="w-10 h-10 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Customer Booking System
              </h2>
              <p className="text-gray-600 mb-6">
                Schedule appointments for new customers. Manage calendar, availability, and booking confirmations.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                  Schedule Appointments
                </span>
                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                  Calendar View
                </span>
                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                  Availability
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats or Additional Info */}
        <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Access</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate("/jobs")}
              className="px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="text-sm font-medium text-gray-900">Jobs List</div>
              <div className="text-xs text-gray-500 mt-1">View all booked jobs</div>
            </button>
            <button
              onClick={() => navigate("/installers")}
              className="px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="text-sm font-medium text-gray-900">Manage Installers</div>
              <div className="text-xs text-gray-500 mt-1">Add/remove installers</div>
            </button>
            <button
              onClick={() => navigate("/assignments")}
              className="px-4 py-3 text-left bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border-2 border-blue-200"
            >
              <div className="text-sm font-medium text-blue-900">Job Assignments</div>
              <div className="text-xs text-blue-600 mt-1">Assign jobs to installers</div>
            </button>
            <button
              onClick={() => navigate("/warranty")}
              className="px-4 py-3 text-left bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors border-2 border-purple-200"
            >
              <div className="text-sm font-medium text-purple-900">Customer Warranty Form</div>
              <div className="text-xs text-purple-600 mt-1">Test customer claim page</div>
            </button>
            <button
              onClick={() => navigate("/ads")}
              className="px-4 py-3 text-left bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors border-2 border-orange-200"
              data-testid="ads-dashboard-link"
            >
              <div className="text-sm font-medium text-orange-900 flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" /> Google Ads Analytics
              </div>
              <div className="text-xs text-orange-600 mt-1">Cost per job & ad spend tracking</div>
            </button>
            <button
              onClick={() => navigate("/calendar")}
              className="px-4 py-3 text-left bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors border-2 border-teal-200"
              data-testid="calendar-link"
            >
              <div className="text-sm font-medium text-teal-900 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> CA Calendar
              </div>
              <div className="text-xs text-teal-600 mt-1">Monthly install schedule</div>
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-gray-500">
        <p>Hybrid2Go Internal Portal - For Staff Use Only</p>
      </div>
    </div>
  );
};

export default HomePage;
